// Composant Modal - Calendrier des Commissions (Lecture Seule)
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { theme } from '@theme';
import {
  FiCalendar, FiX, FiClock, FiMapPin, FiUsers, FiCheckCircle, FiXCircle, FiHome, FiLoader
} from 'react-icons/fi';
import { CommissionServices } from '@services/CommissionServices';

interface Commission {
  id: number;
  title: string;
  reference: string;
  scheduled_date: string;
  location: string;
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
  statistics: {
    total_applications: number;
    retained: number;
    rejected: number;
    waiting: number;
  };
  created_at: string;
}

interface CommissionCalendarModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Styled Components
const Overlay = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing.xl};
  animation: fadeIn 0.2s ease;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.xl};
  max-width: 900px;
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: slideUp 0.3s ease;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(50px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: ${theme.breakpoints.md}) {
    max-width: 100%;
    max-height: 100vh;
    border-radius: 0;
  }
`;

const Header = styled.div`
  padding: ${theme.spacing.xl};
  border-bottom: 1px solid ${theme.colors.surface};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h2`
  font-size: ${theme.typography.sizes.xl};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: ${theme.colors.text}60;
  cursor: pointer;
  padding: ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.surface};
    color: ${theme.colors.text};
  }

  svg {
    font-size: 24px;
  }
`;

const Body = styled.div`
  padding: ${theme.spacing.xl};
  overflow-y: auto;
  flex: 1;
`;

const InfoBanner = styled.div`
  background: ${theme.colors.info}15;
  border: 1px solid ${theme.colors.info};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}80;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const SessionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const SessionCard = styled.div<{ status: string }>`
  padding: ${theme.spacing.lg};
  background: ${props => {
    switch (props.status) {
      case 'completed': return theme.colors.success + '10';
      case 'cancelled': return theme.colors.error + '10';
      default: return theme.colors.surface;
    }
  }};
  border: 1px solid ${props => {
    switch (props.status) {
      case 'completed': return theme.colors.success + '40';
      case 'cancelled': return theme.colors.error + '40';
      default: return theme.colors.surface;
    }
  }};
  border-radius: ${theme.borderRadius.md};
  transition: all ${theme.transitions.fast};

  &:hover {
    box-shadow: ${theme.shadows.sm};
    transform: translateY(-2px);
  }
`;

const SessionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: ${theme.spacing.md};
`;

const SessionDate = styled.div`
  font-size: ${theme.typography.sizes.lg};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.xs};
`;

const StatusBadge = styled.div<{ status: string }>`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.sizes.xs};
  font-weight: ${theme.typography.weights.semibold};
  background: ${props => {
    switch (props.status) {
      case 'completed': return theme.colors.success + '20';
      case 'cancelled': return theme.colors.error + '20';
      default: return theme.colors.info + '20';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'completed': return theme.colors.success;
      case 'cancelled': return theme.colors.error;
      default: return theme.colors.info;
    }
  }};
`;

const SessionDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${theme.spacing.md};
`;

const Detail = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}80;

  svg {
    color: ${theme.colors.primary};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${theme.spacing['2xl']};
  color: ${theme.colors.text}60;

  svg {
    font-size: 48px;
    margin-bottom: ${theme.spacing.md};
    opacity: 0.5;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing['2xl']};
  gap: ${theme.spacing.md};
  color: ${theme.colors.text}60;

  svg {
    font-size: 48px;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

export const CommissionCalendarModal: React.FC<CommissionCalendarModalProps> = ({
  isOpen,
  onClose
}) => {
  const [commissions, setCommissions] = useState<Commission[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadCommissions();
    }
  }, [isOpen]);

  const loadCommissions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await CommissionServices.list() as { success: boolean; data?: Commission[] };
      if (response.success && response.data) {
        // Trier par date décroissante
        const sortedCommissions = response.data.sort((a: Commission, b: Commission) =>
          new Date(b.scheduled_date).getTime() - new Date(a.scheduled_date).getTime()
        );
        setCommissions(sortedCommissions);
      }
    } catch (err: any) {
      console.error('Erreur chargement commissions:', err);
      setError(err.message || 'Erreur lors du chargement des commissions');
    } finally {
      setLoading(false);
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'completed': return 'Terminée';
      case 'cancelled': return 'Annulée';
      case 'in_progress': return 'En cours';
      case 'scheduled': return 'Planifiée';
      default: return 'Planifiée';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <FiCheckCircle />;
      case 'cancelled': return <FiXCircle />;
      default: return <FiCalendar />;
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <Overlay isOpen={isOpen} onClick={handleOverlayClick}>
      <ModalContainer>
        <Header>
          <Title>
            <FiCalendar />
            Calendrier des Commissions
          </Title>
          <CloseButton onClick={onClose}>
            <FiX />
          </CloseButton>
        </Header>

        <Body>
          <InfoBanner>
            <FiCalendar />
            Vous pouvez consulter les sessions de commission planifiées et passées. Pour plus d'informations, contactez un membre de la commission.
          </InfoBanner>

          {loading ? (
            <LoadingContainer>
              <FiLoader />
              <p>Chargement des commissions...</p>
            </LoadingContainer>
          ) : error ? (
            <EmptyState>
              <FiXCircle />
              <p>{error}</p>
            </EmptyState>
          ) : (
            <SessionList>
              {commissions.length > 0 ? (
                commissions.map(commission => (
                  <SessionCard key={commission.id} status={commission.status}>
                    <SessionHeader>
                      <div>
                        <SessionDate>
                          {new Date(commission.scheduled_date).toLocaleDateString('fr-FR', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </SessionDate>
                      </div>
                      <StatusBadge status={commission.status}>
                        {getStatusIcon(commission.status)}
                        {getStatusLabel(commission.status)}
                      </StatusBadge>
                    </SessionHeader>

                    <SessionDetails>
                      <Detail>
                        <FiClock />
                        {new Date(commission.scheduled_date).toLocaleTimeString('fr-FR', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </Detail>
                      <Detail>
                        <FiMapPin />
                        {commission.location}
                      </Detail>
                      <Detail>
                        <FiHome />
                        {commission.title}
                      </Detail>
                      <Detail>
                        <FiUsers />
                        {commission.statistics.total_applications} candidat{commission.statistics.total_applications > 1 ? 's' : ''}
                      </Detail>
                    </SessionDetails>
                  </SessionCard>
                ))
              ) : (
                <EmptyState>
                  <FiCalendar />
                  <p>Aucune session de commission programmée pour le moment</p>
                </EmptyState>
              )}
            </SessionList>
          )}
        </Body>
      </ModalContainer>
    </Overlay>
  );
};
